import { CalendarClock, Megaphone, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { usableTimeZone } from "../../event-experience/agenda-schedule";
import { useAdminFeedbackForms } from "../../feedback/use-feedback";
import type { SeatingAnnouncementDraft } from "../../seatings/announcement-draft";
import { useSeatings } from "../../seatings/use-seatings";
import { LinkifiedText } from "../../notifications/LinkifiedText";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { renderText } from "../announcement-placeholders";
import { findForm, formAtSend, pickerWarning } from "../announcement-form";
import {
  formatSendTime,
  parseSendTime,
  SEND_PRECISION,
} from "../scheduled-announcements";
import { useAdminCommand } from "../use-admin-command";
import {
  AnnouncementPreview,
  PersonalisationWarnings,
  useAnnouncementPersonalisation,
} from "./AnnouncementPreview";
import { PlaceholderChips } from "./PlaceholderChips";
import { FeedbackFormPicker, NO_FORM } from "./FeedbackFormPicker";
import {
  NO_SEATING,
  SeatingPicker,
  seatingProblem,
  usesDinnerTable,
} from "./SeatingPicker";
import { SendTimeNote } from "./SendTimeNote";
import { PersonalisedBadge, TemplateText } from "./TemplateText";
import { ScheduledAnnouncementList } from "./ScheduledAnnouncementList";
import { SentAnnouncementList } from "./SentAnnouncementList";
import { TimeField } from "../WallTimeField";
import { WarningNote } from "./WarningNote";

export const MAX_TITLE = 120;
export const MAX_BODY = 1000;

// The fields start empty; these are examples, never defaults. An organiser read
// the previous bare "Dinner at 19:00" as text already filled in, because the
// placeholder colour sat close to the text colour. The "e.g." prefix and the
// lighter italic style below are what mark them as examples.
export const TITLE_EXAMPLE = "e.g. Dinner starts at 19:00";
export const BODY_EXAMPLE =
  "e.g. Please join us in the main hall. Feedback survey: https://example.com/survey";
export const EXAMPLE_STYLE =
  "placeholder:italic placeholder:text-muted-foreground/70";

type Mode = "now" | "later";

/**
 * Ad-hoc announcements ("dinner at 19:00", "plenary moved to Hall B"), sent
 * now or booked for later.
 *
 * Sending is a command, not a direct write: the trigger fans the message out
 * to every registered member's inbox and, once push ships, to their devices.
 * There is deliberately no recipient picker — the audience is "everyone at
 * this event", and narrowing it is a different feature with different rules.
 *
 * "Send later" files SCHEDULE_EVENT_ANNOUNCEMENT instead. The send time is
 * typed on the event's clock, like agenda times, and the audience is whoever
 * is registered when it goes out, not when it was booked.
 *
 * Either way the announcement can link one of the event's feedback forms, and
 * the notification then carries a button that opens it. The form has to take
 * answers when the announcement goes out: Send now refuses here, before the
 * command, a form that does not; Send later accepts one that is closed today
 * with a warning, and the backend checks again at the send time. The Feedback
 * tab's Announce button stays as a shortcut for an open form.
 *
 * Either way the click opens a confirmation first. The fan-out reaches every
 * member and pushes to their devices, which is the same bar the resource and
 * attendee deletions in this admin area already confirm at. A booked one can
 * still be cancelled until it goes out, and the dialog says so. Once sent,
 * "Delete for everyone" (DELETE_EVENT_ANNOUNCEMENT) removes the in-app copies
 * from both lists below; a push already shown on a device stays.
 */
export function AnnouncementsSection({
  eventId,
  uid,
  timezone,
  published = true,
  draft = null,
  onDraftConsumed,
}: {
  eventId: string;
  uid: string;
  timezone?: string;
  /** Scheduled announcements for an unpublished event are not sent. */
  published?: boolean;
  /** A message to start from, from "Notify guests" on a seating (TNG-94). */
  draft?: SeatingAnnouncementDraft | null;
  /**
   * Called once the draft is in the fields, so the parent can drop it and a
   * later mount of this tab (after sending, say) starts empty.
   */
  onDraftConsumed?: () => void;
}) {
  const timeZone = usableTimeZone(timezone);
  const { state, submit, reset } = useAdminCommand();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mode, setMode] = useState<Mode>("now");
  const [sendAt, setSendAt] = useState("");
  const [formId, setFormId] = useState<string>(NO_FORM);
  // TNG-94: the dinner seating {dinner_table} is filled from.
  const [seatingId, setSeatingId] = useState<string>(NO_SEATING);
  const [problem, setProblem] = useState<string | null>(null);
  const forms = useAdminFeedbackForms(eventId);
  const formList = forms.status === "success" ? forms.data : null;
  const [confirming, setConfirming] = useState<null | {
    mode: Mode;
    instant?: Date;
  }>(null);
  const busy = state.status === "submitting" || state.status === "watching";
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const needsSeating = usesDinnerTable(title, body);
  const seatings = useSeatings(eventId, {
    enabled: needsSeating || draft !== null,
  });
  const seatingList = seatings.status === "success" ? seatings.data : null;
  // Placeholders (TNG-95): a typo blocks sending; a missing value only warns.
  const personalisation = useAnnouncementPersonalisation(
    eventId,
    title,
    body,
    MAX_TITLE,
    MAX_BODY,
    needsSeating && seatingId !== NO_SEATING ? seatingId : null,
  );
  const valid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    personalisation.problem === null &&
    (!needsSeating || seatingId !== NO_SEATING);

  // "Notify guests" on a published seating starts the composer from its
  // message, with that seating chosen. Keyed, so a second click refills it.
  const draftKey = draft?.key;
  useEffect(() => {
    if (!draft) return;
    setTitle(draft.title);
    setBody(draft.body);
    setSeatingId(draft.seatingId);
    setMode("now");
    setProblem(null);
    onDraftConsumed?.();
    // Only a new draft refills; typing afterwards is the organiser's.
  }, [draftKey]);

  // Clear only once the command actually SUCCEEDED: the outcome is
  // asynchronous, and wiping on click would lose the composed message when
  // the command fails — the inputs stay disabled while it is in flight.
  useEffect(() => {
    if (state.status === "succeeded") {
      setTitle("");
      setBody("");
      setSendAt("");
      setFormId(NO_FORM);
      setSeatingId(NO_SEATING);
    }
  }, [state.status]);

  const chosenForm =
    formId === NO_FORM ? undefined : findForm(formList, formId);
  // When the picker judges the form: now, or the typed send time once it is
  // a valid one.
  const parsedSendAt = parseSendTime(sendAt, timeZone, new Date());
  const pickerAt =
    mode === "now"
      ? new Date()
      : "instant" in parsedSendAt
        ? parsedSendAt.instant
        : null;

  const confirmWarning =
    confirming?.mode === "later" && formId !== NO_FORM && formList !== null
      ? pickerWarning(chosenForm, "later", confirming.instant ?? null, timeZone)
      : null;

  function review() {
    if (!valid || busy) return;
    if (needsSeating && mode === "now") {
      // The fan-out refuses an unpublished or deleted seating; saying so here
      // is kinder than a failed send.
      const seatingIssue = seatingProblem(seatingList, seatingId, "now");
      if (seatingIssue) {
        setProblem(seatingIssue);
        return;
      }
    }
    if (mode === "now") {
      // The fan-out would refuse it anyway; saying so here keeps the
      // organiser from sending a notification-less failure.
      if (
        formId !== NO_FORM &&
        formList !== null &&
        formAtSend(chosenForm, new Date()) !== "accepting"
      ) {
        setProblem(pickerWarning(chosenForm, "now", new Date(), timeZone));
        return;
      }
      setProblem(null);
      setConfirming({ mode: "now" });
      return;
    }
    const parsed = parseSendTime(sendAt, timeZone, new Date());
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    setProblem(null);
    setConfirming({ mode: "later", instant: parsed.instant });
  }

  function confirm() {
    if (!valid || busy || !confirming) return;
    const message = {
      title: title.trim(),
      body: body.trim(),
      ...(formId === NO_FORM ? {} : { feedbackFormId: formId }),
      ...(needsSeating && seatingId !== NO_SEATING ? { seatingId } : {}),
    };
    if (confirming.mode === "now") {
      setConfirming(null);
      void submit("SEND_EVENT_ANNOUNCEMENT", eventId, uid, message);
      return;
    }
    // Checked again: the dialog may have sat open past the time, and the
    // rules would then refuse the write with an error that names nothing.
    const parsed = parseSendTime(sendAt, timeZone, new Date());
    setConfirming(null);
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    void submit("SCHEDULE_EVENT_ANNOUNCEMENT", eventId, uid, {
      ...message,
      sendAt: parsed.instant,
    });
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="announcements-content">
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Megaphone className="h-4 w-4 shrink-0" />
          Sends a notification to everyone registered for this event. Once sent,
          you can delete it from everyone&rsquo;s inbox, but a push notification
          already shown on a device stays there.
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="announcement-title">Title</Label>
          <Input
            ref={titleRef}
            id="announcement-title"
            testId="announcement-title"
            placeholder={TITLE_EXAMPLE}
            className={EXAMPLE_STYLE}
            value={title}
            maxLength={MAX_TITLE}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
          />
          <PlaceholderChips
            target={titleRef}
            value={title}
            onChange={setTitle}
            maxLength={MAX_TITLE}
            disabled={busy}
            testId="announcement-title-chips"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="announcement-body">Message</Label>
          <Textarea
            ref={bodyRef}
            id="announcement-body"
            data-testid="announcement-body"
            placeholder={BODY_EXAMPLE}
            className={EXAMPLE_STYLE}
            value={body}
            maxLength={MAX_BODY}
            disabled={busy}
            rows={4}
            onChange={(e) => setBody(e.target.value)}
          />
          <PlaceholderChips
            target={bodyRef}
            value={body}
            onChange={setBody}
            maxLength={MAX_BODY}
            disabled={busy}
            testId="announcement-body-chips"
          />
          <p className="text-xs text-muted-foreground">
            Links starting with https:// or http:// become clickable in the app.
            Each person sees their own details in place of a placeholder; write{" "}
            {"{room|the foyer}"} to choose what they see when they have none.{" "}
            {"{champion}"} lists both champions of the track, joined with "and".{" "}
            {body.length}/{MAX_BODY}
          </p>
        </div>

        {personalisation.problem && (
          <p
            className="text-sm text-destructive-text"
            role="alert"
            data-testid="announcement-placeholder-problem"
          >
            {personalisation.problem}
          </p>
        )}
        <AnnouncementPreview
          title={title}
          body={body}
          maxTitle={MAX_TITLE}
          maxBody={MAX_BODY}
          personalisation={personalisation}
        />
        <PersonalisationWarnings
          personalisation={personalisation}
          testId="announcement-warnings"
        />

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">When</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {(
              [
                ["now", "Send now"],
                ["later", "Send later"],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0"
              >
                <input
                  type="radio"
                  name="announcement-when"
                  value={value}
                  data-testid={`announcement-when-${value}`}
                  checked={mode === value}
                  disabled={busy}
                  onChange={() => {
                    setMode(value);
                    setProblem(null);
                  }}
                  className="h-4 w-4 accent-lit"
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        {mode === "later" && (
          <div className="space-y-2">
            <TimeField
              label="Send at"
              testId="announcement-send-at"
              value={sendAt}
              onChange={(value) => {
                setSendAt(value);
                setProblem(null);
              }}
            />
            <SendTimeNote
              timeZone={timeZone}
              wall={sendAt}
              testId="announcement-send-at-clock"
            />
            {!published && (
              <WarningNote testId="announcement-unpublished">
                This event is not published. A scheduled announcement only goes
                out if the event is published by its send time; otherwise it is
                marked as not sent.
              </WarningNote>
            )}
          </div>
        )}

        {needsSeating && (
          <SeatingPicker
            id="announcement-seating"
            testId="announcement-seating-select"
            seatings={seatingList}
            value={seatingId}
            onChange={(value) => {
              setSeatingId(value);
              setProblem(null);
            }}
            mode={mode}
            disabled={busy}
          />
        )}

        <FeedbackFormPicker
          id="announcement-form"
          testId="announcement-form"
          forms={formList}
          value={formId}
          onChange={(value) => {
            setFormId(value);
            setProblem(null);
          }}
          mode={mode}
          at={pickerAt}
          timeZone={timeZone}
          disabled={busy}
        />

        {problem && (
          <p
            className="text-sm text-destructive-text"
            role="alert"
            data-testid="announcement-problem"
          >
            {problem}
          </p>
        )}

        <Button
          variant="cta"
          onClick={review}
          disabled={busy || !valid}
          data-testid="send-announcement"
        >
          {mode === "now" ? (
            <Send className="h-4 w-4" />
          ) : (
            <CalendarClock className="h-4 w-4" />
          )}
          {mode === "now" ? "Send announcement" : "Schedule announcement"}
        </Button>

        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="announcement"
        />
      </div>

      <ScheduledAnnouncementList
        eventId={eventId}
        uid={uid}
        timeZone={timeZone}
        forms={formList}
      />

      <SentAnnouncementList
        eventId={eventId}
        uid={uid}
        timeZone={timeZone}
        forms={formList}
      />

      <Dialog
        open={confirming !== null}
        onOpenChange={(next) => !next && setConfirming(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirming?.mode === "later"
                ? "Schedule this announcement?"
                : "Send this announcement?"}
            </DialogTitle>
          </DialogHeader>
          {confirming?.mode === "later" ? (
            <div
              className="space-y-2 text-sm text-muted-foreground"
              data-testid="confirm-schedule-text"
            >
              <p>
                It goes out on{" "}
                <span className="font-medium text-foreground">
                  {formatSendTime(confirming.instant ?? null, timeZone)}
                </span>
                . {SEND_PRECISION}
              </p>
              <p>
                Everyone registered for this event at that time gets a
                notification. You can edit or cancel it until then. Once sent, a
                push notification shown on a device cannot be recalled.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Everyone registered for this event gets a notification. You can
              delete it from everyone&rsquo;s inbox later, but a push
              notification already shown on a device cannot be recalled.
            </p>
          )}
          {personalisation.personalised ? (
            <div
              className="space-y-2 rounded-lg border border-border bg-muted px-3 py-2"
              data-testid="confirm-personalised"
            >
              <PersonalisedBadge title={title} body={body} />
              <p className="text-sm font-semibold text-foreground wrap-anywhere">
                <TemplateText text={title.trim()} />
              </p>
              <p className="whitespace-pre-line text-sm text-muted-foreground wrap-anywhere">
                <TemplateText text={body.trim()} />
              </p>
              <p
                className="text-xs text-muted-foreground"
                data-testid="confirm-personalised-note"
              >
                {confirming?.mode === "later"
                  ? "Each person\u2019s details are filled in when it goes out, from their track and attendee record at that time."
                  : "Each person sees their own details in place of the highlighted placeholders."}
              </p>
            </div>
          ) : (
            // Rendered, so {{ and }} read as the single braces people get.
            <div className="rounded-lg border border-border bg-muted px-3 py-2">
              <p className="text-sm font-semibold text-foreground wrap-anywhere">
                {renderText(title.trim(), {}, MAX_TITLE)}
              </p>
              <p className="whitespace-pre-line text-sm text-muted-foreground wrap-anywhere">
                <LinkifiedText text={renderText(body.trim(), {}, MAX_BODY)} />
              </p>
            </div>
          )}
          <PersonalisationWarnings
            personalisation={personalisation}
            testId="confirm-warnings"
          />
          {formId !== NO_FORM && (
            <p
              className="mt-1 text-sm text-muted-foreground"
              data-testid="confirm-announcement-form"
            >
              Feedback form:{" "}
              <span className="font-medium text-foreground">
                {chosenForm?.title ?? "Deleted form"}
              </span>
            </p>
          )}
          {confirmWarning && (
            <WarningNote testId="confirm-announcement-form-warning">
              {confirmWarning}
            </WarningNote>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="cta"
              disabled={busy || !valid}
              data-testid="confirm-send-announcement"
              onClick={confirm}
            >
              {confirming?.mode === "later" ? (
                <>
                  <CalendarClock className="h-4 w-4" />
                  Schedule
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send to everyone
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
